import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Newspaper, Link2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function PressDialog({ open, onOpenChange, songId, onAdded }) {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => setUrl("");

  const submit = async () => {
    const link = url.trim();
    if (!/^https?:\/\//i.test(link)) {
      return toast.error("Paste a full article URL starting with http:// or https://");
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/songs/${songId}/press/import-link`, { url: link });
      toast.success(data.duplicate ? "That article is already added" : `Added “${data.press?.outlet || "press"}”`);
      onOpenChange(false);
      reset();
      onAdded?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="press-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl"><Newspaper className="h-5 w-5 text-[#35606E]" /> Add press</DialogTitle>
          <DialogDescription>Paste a press article link — we'll pull the headline, outlet, date and image. It attaches to this song and rolls up to the artist.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Article URL</Label>
            <div className="relative mt-1">
              <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <Input
                data-testid="press-link"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="https://www.clashmusic.com/…"
                className="pl-9"
              />
            </div>
          </div>
          <Button data-testid="submit-press" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#35606E] py-6 hover:bg-[#294b57]">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Add press
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
